import { ceil, round } from 'lodash';
import {
    flipPoint,
    getLineIntegerPoints,
    pointScale,
    pointsOutOfRangeRectangle,
    rotatePoint,
    translatePointMove,
    translateYReverse,
} from '@/utils/math-kit';
import { IImageLayerProps, ILayerProps, IPoint } from '@/version-types';

const getImageTransformInfo = (layer: ILayerProps) => {
    const { width, height } = layer;
    const {
        imageTransform: { widthRatio, heightRatio, offsetX: offsetXRatio, offsetY: offsetYRatio, scale },
    } = layer.data as IImageLayerProps;

    const originImgWidth = width * widthRatio;
    const originImgHeight = height * heightRatio;
    const offsetX = offsetXRatio * originImgWidth * scale;
    const offsetY = offsetYRatio * originImgHeight * scale;

    return { offsetX, offsetY, originImgWidth, originImgHeight };
};

export const getCornerPositions = (width: number, height: number) => {
    return {
        leftTop: { x: -width / 2, y: height / 2 },
        rightTop: { x: width / 2, y: height / 2 },
        leftBottom: { x: -width / 2, y: -height / 2 },
        rightBottom: { x: width / 2, y: -height / 2 },
    };
};

export const transformLayerPositionToImagePoint = (point: IPoint, layer: ILayerProps) => {
    const {
        imageTransform: { flipH, flipV, rotate },
    } = layer.data as IImageLayerProps;
    const { offsetX, offsetY } = getImageTransformInfo(layer);

    const rotateP = rotatePoint(point, rotate);
    const flipP = flipPoint(rotateP, flipH, flipV);
    const offset = flipPoint(rotatePoint({ x: -offsetX, y: offsetY }, rotate), flipH, flipV);
    return translatePointMove(flipP, offset.x, offset.y);
};

export const getCornerInfos = (layer: ILayerProps) => {
    const {
        imageTransform: { scale: imageScale },
    } = layer.data as IImageLayerProps;
    const { originImgWidth, originImgHeight } = getImageTransformInfo(layer);

    let { leftTop, rightTop, leftBottom, rightBottom } = getCornerPositions(layer.width, layer.height);
    leftTop = transformLayerPositionToImagePoint(leftTop, layer);
    rightTop = transformLayerPositionToImagePoint(rightTop, layer);
    leftBottom = transformLayerPositionToImagePoint(leftBottom, layer);
    rightBottom = transformLayerPositionToImagePoint(rightBottom, layer);

    const imageCornerPosition = getCornerPositions(originImgWidth * imageScale, originImgHeight * imageScale);
    return {
        imageCornerPosition,
        layerCornerPosition: { leftTop, rightTop, leftBottom, rightBottom },
    };
};

export const isImageOutOfRangeLayer = (layer: ILayerProps) => {
    const { imageCornerPosition, layerCornerPosition } = getCornerInfos(layer);
    return pointsOutOfRangeRectangle(
        [
            layerCornerPosition.leftTop,
            layerCornerPosition.rightTop,
            layerCornerPosition.leftBottom,
            layerCornerPosition.rightBottom,
        ],
        imageCornerPosition,
    );
};

export const getMovePositionInImageCenter = (layer: ILayerProps, moveDistance: IPoint, stageScale: number) => {
    const { rotate, data } = layer;
    const { imageTransform } = data as IImageLayerProps;
    const { flipH, flipV, rotate: imageRotate } = imageTransform;

    return flipPoint(
        rotatePoint(translateYReverse(pointScale(moveDistance, 1 / stageScale)), imageRotate + rotate),
        flipH,
        flipV,
    );
};

export const calculateProjectionPoint = (aPoint: IPoint, bPoint: IPoint, cPoint: IPoint) => {
    const AB = { x: bPoint.x - aPoint.x, y: bPoint.y - aPoint.y };
    const BC = { x: cPoint.x - bPoint.x, y: cPoint.y - bPoint.y };
    const abLength = Math.sqrt(AB.x * AB.x + AB.y * AB.y);
    const abUnit = { x: AB.x / abLength, y: AB.y / abLength };
    const projectionLength = BC.x * abUnit.x + BC.y * abUnit.y;

    return {
        x: bPoint.x + abUnit.x * projectionLength,
        y: bPoint.y + abUnit.y * projectionLength,
    };
};

export const getMoveDistanceByMoveType = (layer: ILayerProps, scalePosition: string, delta: IPoint) => {
    const { width, height, data } = layer;
    const { imageTransform } = data as IImageLayerProps;
    const { widthRatio, heightRatio, scale, rotate: imageRotate } = imageTransform;
    const imageWidth = ceil(width * widthRatio * scale);
    const imageHeight = ceil(height * heightRatio * scale);

    let movePoint = { x: 0, y: 0 };
    if (scalePosition.includes('lt')) {
        movePoint = { x: -imageWidth, y: imageHeight };
    } else if (scalePosition.includes('rb')) {
        movePoint = { x: imageWidth, y: -imageHeight };
    } else if (scalePosition.includes('lb')) {
        movePoint = { x: -imageWidth, y: -imageHeight };
    } else if (scalePosition.includes('rt')) {
        movePoint = { x: imageWidth, y: imageHeight };
    }

    const endPoint = {
        x: movePoint.x + Math.round(delta.x),
        y: movePoint.y + Math.round(delta.y),
    };
    const moveDistancePoint = calculateProjectionPoint({ x: 0, y: 0 }, movePoint, endPoint);
    const points = getLineIntegerPoints(
        movePoint.x,
        movePoint.y,
        round(moveDistancePoint.x),
        round(moveDistancePoint.y),
    );

    const newSizes = points.map((item) => ({
        width: Math.abs(item.x),
        height: Math.abs(item.y),
    }));

    const movePoints = points.map((item) =>
        translateYReverse(
            rotatePoint(
                {
                    x: -(movePoint.x - item.x) / 2,
                    y: -(movePoint.y - item.y) / 2,
                },
                -imageRotate,
            ),
        ),
    );

    return { newSizes, movePoints };
};

export const getLayerMoveDistanceByMoveResizeType = (layer: ILayerProps, resizePosition: string, delta: IPoint) => {
    const { width, height } = layer;
    const movePoint =
        resizePosition.includes('lc') || resizePosition.includes('rc') ? { x: delta.x, y: 0 } : { x: 0, y: delta.y };

    const points = getLineIntegerPoints(0, 0, movePoint.x, movePoint.y);
    const newSizes = points.map((item) => {
        if (resizePosition.includes('lc')) {
            return { width: width - item.x, height };
        }
        if (resizePosition.includes('rc')) {
            return { width: width + item.x, height };
        }
        if (resizePosition.includes('tc')) {
            return { width, height: height + item.y };
        }
        if (resizePosition.includes('bc')) {
            return { width, height: height - item.y };
        }
        return { width, height };
    });

    const movePoints = points.map((item) => translateYReverse({ x: item.x / 2, y: item.y / 2 }));
    return { newSizes, movePoints };
};

export const getLayerMoveDistanceByMoveType = (
    layer: ILayerProps,
    scalePosition: string,
    delta: IPoint,
    isAutoCutoutLayer: boolean,
) => {
    const { width, height } = layer;

    let movePoint = { x: 0, y: 0 };
    if (scalePosition.includes('lt')) {
        movePoint = { x: -width, y: height };
    } else if (scalePosition.includes('rb')) {
        movePoint = { x: width, y: -height };
    } else if (scalePosition.includes('lb')) {
        movePoint = { x: -width, y: -height };
    } else if (scalePosition.includes('rt')) {
        movePoint = { x: width, y: height };
    }

    const endPoint = {
        x: movePoint.x + Math.round(delta.x),
        y: movePoint.y + Math.round(delta.y),
    };
    const moveDistancePoint = calculateProjectionPoint({ x: 0, y: 0 }, movePoint, endPoint);
    const points = getLineIntegerPoints(
        movePoint.x,
        movePoint.y,
        !isAutoCutoutLayer ? round(moveDistancePoint.x) : endPoint.x,
        !isAutoCutoutLayer ? round(moveDistancePoint.y) : endPoint.y,
    );

    const newSizes = points.map((item) => ({
        width: Math.abs(item.x),
        height: Math.abs(item.y),
    }));

    const movePoints = points.map((item) =>
        translateYReverse({
            x: -(movePoint.x - item.x) / 2,
            y: -(movePoint.y - item.y) / 2,
        }),
    );

    return { newSizes, movePoints };
};

export const getCenterPoint = (element: HTMLElement) => {
    const { left, top, width, height } = element.getBoundingClientRect();
    return { x: left + width / 2, y: top + height / 2 };
};
